import { useState, type ReactElement } from 'react';
import { Box, Button, Divider, Group, Popover, Stack, Text, TextInput } from '@mantine/core';
import { Calendar } from 'lucide-react';
import { DATE_RANGE_PRESETS, type DateRangePreset, type GlobalDateRange } from './GlobalFilterTypes';

interface GlobalDateRangeControlProps {
    range: GlobalDateRange;
    onChange: (range: GlobalDateRange) => void;
}

/** The date-range control in the global filter bar: preset list plus a custom start/end range. */
export default function GlobalDateRangeControl({ range, onChange }: GlobalDateRangeControlProps): ReactElement {
    const [opened, setOpened] = useState(false);
    const [customStart, setCustomStart] = useState<string>(range.start ?? '');
    const [customEnd, setCustomEnd] = useState<string>(range.end ?? '');

    const pickPreset = (preset: DateRangePreset): void => {
        onChange({ kind: 'preset', preset, start: null, end: null });
        setOpened(false);
    };

    const applyCustom = (): void => {
        if (customStart.length === 0 || customEnd.length === 0) return;
        const [start, end] = customStart <= customEnd ? [customStart, customEnd] : [customEnd, customStart];
        onChange({ kind: 'custom', preset: null, start, end });
        setOpened(false);
    };

    return (
        <Popover opened={opened} onChange={setOpened} position="bottom-start" width={300} shadow="md" radius="md" withinPortal trapFocus>
            <Popover.Target>
                <Button
                    size="sm"
                    radius="md"
                    variant="default"
                    fw={500}
                    leftSection={<Calendar size={15} />}
                    onClick={() => setOpened((current) => !current)}
                >
                    {dateRangeLabel(range)}
                </Button>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap="xs">
                    <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
                        Date range
                    </Text>
                    <Stack gap={4}>
                        {DATE_RANGE_PRESETS.map((preset) => (
                            <Button
                                key={preset.value}
                                size="compact-sm"
                                justify="flex-start"
                                variant={range.kind === 'preset' && range.preset === preset.value ? 'light' : 'subtle'}
                                onClick={() => pickPreset(preset.value)}
                            >
                                {preset.label}
                            </Button>
                        ))}
                    </Stack>
                    <Divider label="Custom range" labelPosition="center" />
                    <Group gap="xs" grow>
                        <TextInput aria-label="Start date" type="date" size="xs" value={customStart} onChange={(event) => setCustomStart(event.currentTarget.value)} />
                        <TextInput aria-label="End date" type="date" size="xs" value={customEnd} onChange={(event) => setCustomEnd(event.currentTarget.value)} />
                    </Group>
                    <Box>
                        <Button size="compact-sm" fullWidth onClick={applyCustom} disabled={customStart.length === 0 || customEnd.length === 0}>
                            Apply custom range
                        </Button>
                    </Box>
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
}

/** Short label for the current range, shown on the control's button. */
export function dateRangeLabel(range: GlobalDateRange): string {
    if (range.kind === 'custom' && range.start !== null && range.end !== null) {
        return `${range.start} → ${range.end}`;
    }
    const preset = DATE_RANGE_PRESETS.find((entry) => entry.value === range.preset);
    return preset?.label ?? 'Date range';
}
